import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isSupabaseConfigured, supabase } from '../services/supabaseClient';
import {
  bulkCreateAuthUsersWithProfiles,
  createAuthUserWithProfile,
  emptyProductionState,
  ensureLmsProfileForAuthUser,
  loadLmsStateFromSupabase,
  saveLearningMutationToSupabase,
  saveLmsStateToSupabase,
  testSupabaseConnection
} from '../services/supabaseStateStore';
import {
  assignmentGrade,
  byId,
  certificateNumber,
  getAssignmentProgress,
  getCourse,
  getExercisesForVersion,
  getLessonsForVersion,
  getQuizzesForVersion,
  getQuizAvailability,
  getProgramProgress,
  makeId,
  quizPassed,
  verificationCode
} from '../utils/lmsSelectors';

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function emptyLessonProgress() {
  return { status: 'not_started', acknowledged: false, submissionText: '', submissionUrl: '', feedback: '' };
}

function normalizeLessonProgress(data, courseVersionId) {
  const lessons = getLessonsForVersion(data, courseVersionId);
  return lessons.reduce((progress, lesson) => {
    progress[lesson.id] = emptyLessonProgress();
    return progress;
  }, {});
}

function resetQuizLearningScope(next, assignment, quiz) {
  const lessonIdsToReset = new Set();
  const quizIdsToReset = new Set([quiz.id]);
  const scope = {
    type: 'quiz',
    title: quiz.title,
    lessonCount: 0,
    quizCount: 1,
    targetLessonId: quiz.afterLessonId || '',
    targetModuleId: quiz.moduleId || ''
  };

  if (quiz.afterLessonId) {
    lessonIdsToReset.add(quiz.afterLessonId);
    const lesson = next.lessons.find((item) => item.id === quiz.afterLessonId);
    scope.type = 'lesson';
    scope.title = lesson?.title || quiz.title;
  } else if (quiz.moduleId) {
    const module = next.modules.find((item) => item.id === quiz.moduleId);
    scope.type = 'module';
    scope.title = module?.title || quiz.title;
    next.lessons
      .filter((lesson) => lesson.moduleId === quiz.moduleId)
      .forEach((lesson) => lessonIdsToReset.add(lesson.id));
    next.quizzes
      .filter((item) => item.courseVersionId === quiz.courseVersionId)
      .filter((item) => item.moduleId === quiz.moduleId || lessonIdsToReset.has(item.afterLessonId))
      .forEach((item) => quizIdsToReset.add(item.id));
  }

  assignment.lessonProgress = assignment.lessonProgress || {};
  lessonIdsToReset.forEach((lessonId) => {
    assignment.lessonProgress[lessonId] = emptyLessonProgress();
  });

  assignment.quizAttempts = (assignment.quizAttempts || []).filter((attempt) => !quizIdsToReset.has(attempt.quizId));
  assignment.quizAnswerDrafts = assignment.quizAnswerDrafts || {};
  quizIdsToReset.forEach((quizId) => {
    delete assignment.quizAnswerDrafts[quizId];
  });

  scope.lessonCount = lessonIdsToReset.size;
  scope.quizCount = quizIdsToReset.size;
  return scope;
}

function latestExerciseSubmission(assignment, exerciseId) {
  const submissions = assignment.exerciseSubmissions?.filter((item) => item.exerciseId === exerciseId) || [];
  return submissions.sort((a, b) => Number(b.attemptNumber || 0) - Number(a.attemptNumber || 0))[0];
}

function addAudit(next, actorUserId, action, entityType, entityId) {
  const audit = {
    id: makeId('audit'),
    actorUserId,
    action,
    entityType,
    entityId,
    createdAt: new Date().toISOString()
  };
  next.auditLogs = [audit, ...(next.auditLogs || [])];
  return audit;
}

function courseHasActiveAssignments(next, courseId) {
  return next.assignments.some((assignment) => assignment.courseId === courseId && assignment.status !== 'revoked');
}

function versionHasActiveAssignments(next, courseVersionId) {
  return next.assignments.some((assignment) => assignment.courseVersionId === courseVersionId && assignment.status !== 'revoked');
}

function normalizedRoles(inputRoles, fallbackRole = 'learner') {
  const allowed = ['learner', 'trainer', 'admin'];
  const roles = Array.isArray(inputRoles) ? inputRoles : [fallbackRole];
  const clean = allowed.filter((role) => roles.includes(role));
  return clean.length ? clean : ['learner'];
}

function normalizeUserAccess(user) {
  const roles = normalizedRoles(user.roles, user.role);
  user.roles = roles;
  if (!roles.includes(user.role)) user.role = roles[0];
  user.title = user.title || user.role;
}

function userCanBe(user, role) {
  if (!user) return false;
  return normalizedRoles(user.roles, user.role).includes(role);
}

function reindexSortOrder(items) {
  items.sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0));
  items.forEach((item, index) => {
    item.sortOrder = index + 1;
  });
}

function recalculateAssignment(next, assignmentId) {
  const assignment = next.assignments.find((item) => item.id === assignmentId);
  if (!assignment || assignment.status === 'revoked') return;

  const progress = getAssignmentProgress(next, assignment);
  if (progress.total > 0 && progress.completed === progress.total && !progress.manualPending) {
    assignment.status = 'completed';
    assignment.completedAt = assignment.completedAt || new Date().toISOString();
    const course = getCourse(next, assignment.courseId);
    const existingCert = next.certificates.find((cert) => cert.assignmentId === assignment.id);
    if (course?.certificateEnabled && !existingCert) {
      next.certificates.push({
        id: makeId('cert'),
        assignmentId: assignment.id,
        userId: assignment.assignedToUserId,
        courseId: assignment.courseId,
        courseVersionId: assignment.courseVersionId,
        certificateNumber: certificateNumber(course),
        verificationCode: verificationCode(),
        issuedAt: new Date().toISOString(),
        expiresAt: null,
        status: 'active'
      });
    }
    if (assignment.programAssignmentId) {
      const programAssignment = next.programAssignments?.find((item) => item.id === assignment.programAssignmentId);
      if (programAssignment) {
        const programProgress = getProgramProgress(next, programAssignment);
        programAssignment.status = programProgress.status;
        programAssignment.completedAt = programProgress.status === 'completed' ? (programAssignment.completedAt || new Date().toISOString()) : null;
      }
    }
    return;
  }

  if (progress.manualPending) {
    assignment.status = 'needs_review';
    if (assignment.programAssignmentId) {
      const programAssignment = next.programAssignments?.find((item) => item.id === assignment.programAssignmentId);
      if (programAssignment) programAssignment.status = getProgramProgress(next, programAssignment).status;
    }
    return;
  }

  const anyLessonStarted = Object.values(assignment.lessonProgress || {}).some((item) => item.status !== 'not_started');
  const anyQuizStarted = (assignment.quizAttempts || []).length > 0;
  const anyQuizDraftSaved = Object.keys(assignment.quizAnswerDrafts || {}).length > 0;
  assignment.status = anyLessonStarted || anyQuizStarted || anyQuizDraftSaved ? 'in_progress' : 'not_started';
  assignment.completedAt = null;
  if (assignment.programAssignmentId) {
    const programAssignment = next.programAssignments?.find((item) => item.id === assignment.programAssignmentId);
    if (programAssignment) {
      const programProgress = getProgramProgress(next, programAssignment);
      programAssignment.status = programProgress.status;
      programAssignment.completedAt = programProgress.status === 'completed' ? (programAssignment.completedAt || new Date().toISOString()) : null;
    }
  }
}

const LmsContext = createContext(null);

export function LmsProvider({ children }) {
  const [data, setData] = useState(() => clone(emptyProductionState));
  const [currentUserId, setCurrentUserId] = useState('');
  const [authSession, setAuthSession] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [storageStatus, setStorageStatus] = useState({
    mode: 'supabase-auth',
    message: 'Connecting to Supabase Auth and LMS production database...',
    lastSyncedAt: null,
    error: ''
  });

  const clearLocalSession = useCallback(() => {
    setAuthSession(null);
    setCurrentUserId('');
    setData(clone(emptyProductionState));
    setStorageStatus((previous) => ({
      ...previous,
      message: 'Signed out. Supabase Auth session is required to load LMS data.',
      error: ''
    }));
  }, []);

  const loadDataForSession = useCallback(async (session) => {
    if (!session?.user) {
      clearLocalSession();
      return { ok: false, message: 'No Supabase Auth session.' };
    }

    const profileResult = await ensureLmsProfileForAuthUser(session.user);
    if (!profileResult.ok) {
      setStorageStatus({
        mode: 'supabase-auth',
        message: 'Supabase Auth profile could not load.',
        lastSyncedAt: null,
        error: profileResult.message
      });
      return profileResult;
    }

    if (profileResult.user.status !== 'active') {
      await supabase.auth.signOut();
      clearLocalSession();
      return { ok: false, message: 'This LMS account is inactive.' };
    }

    const result = await loadLmsStateFromSupabase();
    if (!result.ok) {
      setStorageStatus({
        mode: 'supabase-auth',
        message: 'Supabase LMS database could not load.',
        lastSyncedAt: null,
        error: result.message
      });
      return result;
    }

    const loadedData = clone(result.data);
    loadedData.users.forEach(normalizeUserAccess);
    loadedData.courses = (loadedData.courses || []).map((course) => ({ ...course, trainerIds: Array.isArray(course.trainerIds) ? course.trainerIds : [] }));
    loadedData.programs = loadedData.programs || [];
    loadedData.programCourses = loadedData.programCourses || [];
    loadedData.programAssignments = loadedData.programAssignments || [];
    const profile = loadedData.users.find((user) => user.authUserId === session.user.id)
      || loadedData.users.find((user) => user.email?.toLowerCase() === session.user.email?.toLowerCase())
      || profileResult.user;
    normalizeUserAccess(profile);
    setAuthSession(session);
    setData(loadedData);
    setCurrentUserId(profile.id);
    setStorageStatus({
      mode: 'supabase-auth',
      message: `${result.message} Authenticated as ${profile.email}.`,
      lastSyncedAt: new Date().toISOString(),
      error: ''
    });
    return { ok: true, user: profile };
  }, [clearLocalSession]);

  useEffect(() => {
    let active = true;

    async function initializeRemoteState() {
      if (!isSupabaseConfigured || !supabase) {
        setStorageStatus({
          mode: 'supabase-auth',
          message: 'Supabase is required for this production build.',
          lastSyncedAt: null,
          error: 'Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY.'
        });
        setIsInitializing(false);
        return;
      }

      const { data: sessionData, error } = await supabase.auth.getSession();
      if (!active) return;
      if (error) {
        setStorageStatus({
          mode: 'supabase-auth',
          message: 'Supabase Auth session could not load.',
          lastSyncedAt: null,
          error: error.message
        });
        setIsInitializing(false);
        return;
      }

      if (sessionData.session) {
        await loadDataForSession(sessionData.session);
      } else {
        clearLocalSession();
      }
      if (active) setIsInitializing(false);
    }

    initializeRemoteState();

    const { data: authListener } = supabase?.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === 'SIGNED_OUT' || !session) {
        clearLocalSession();
        return;
      }
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        setAuthSession(session);
      }
    }) || { data: null };

    return () => {
      active = false;
      authListener?.subscription?.unsubscribe();
    };
  }, [clearLocalSession, loadDataForSession]);

  const persist = (next) => {
    setData(next);

    if (!isSupabaseConfigured || !authSession) {
      setStorageStatus({
        mode: 'supabase-auth',
        message: 'Supabase Auth session is required for this production build.',
        lastSyncedAt: null,
        error: 'Sign in with Supabase Auth before saving LMS data.'
      });
      return;
    }

    saveLmsStateToSupabase(next).then((result) => {
      if (result.ok) {
        setStorageStatus({
          mode: 'supabase-auth',
          message: result.message,
          lastSyncedAt: new Date().toISOString(),
          error: ''
        });
      } else {
        setStorageStatus({
          mode: 'supabase-auth',
          message: 'Supabase save failed.',
          lastSyncedAt: null,
          error: result.message
        });
      }
    });
  };

  const persistLearningMutation = async (next, assignmentId, auditLogs = []) => {
    setData(next);

    if (!isSupabaseConfigured || !authSession) {
      const result = { ok: false, message: 'Sign in with Supabase Auth before saving learner progress.' };
      setStorageStatus({
        mode: 'supabase-auth',
        message: 'Supabase Auth session is required for this production build.',
        lastSyncedAt: null,
        error: result.message
      });
      return result;
    }

    const result = await saveLearningMutationToSupabase(next, assignmentId, auditLogs);
    if (result.ok) {
      setStorageStatus({
        mode: 'supabase-auth',
        message: result.message,
        lastSyncedAt: new Date().toISOString(),
        error: ''
      });
    } else {
      setStorageStatus({
        mode: 'supabase-auth',
        message: 'Supabase learner progress save failed.',
        lastSyncedAt: null,
        error: result.message
      });
    }
    return result;
  };

  const currentUser = useMemo(() => data.users.find((user) => user.id === currentUserId) || null, [data.users, currentUserId]);

  const login = async ({ email, password }) => {
    if (!isSupabaseConfigured || !supabase) {
      return { ok: false, message: 'Supabase is not configured.' };
    }
    const cleaned = email.trim().toLowerCase();
    const { data: signInData, error } = await supabase.auth.signInWithPassword({ email: cleaned, password });
    if (error || !signInData.session) {
      return { ok: false, message: error?.message || 'Invalid login or inactive user.' };
    }
    const result = await loadDataForSession(signInData.session);
    if (!result.ok) {
      await supabase.auth.signOut();
      return result;
    }
    return { ok: true, user: result.user };
  };

  const logout = async () => {
    if (supabase) await supabase.auth.signOut();
    clearLocalSession();
  };

  const createUser = async (input, actorId) => {
    if (data.users.some((user) => user.email.toLowerCase() === input.email.toLowerCase())) {
      return { ok: false, message: 'Email already exists.' };
    }

    const result = await createAuthUserWithProfile({
      ...input,
      role: normalizedRoles(input.roles, input.role)[0],
      roles: normalizedRoles(input.roles, input.role)
    });
    if (!result.ok) return result;

    const next = clone(data);
    const user = {
      ...result.user,
      role: input.role && result.user.roles?.includes(input.role) ? input.role : result.user.role,
      roles: normalizedRoles(input.roles, input.role)
    };
    normalizeUserAccess(user);
    next.users.push(user);
    addAudit(next, actorId, 'created_auth_user', 'user', user.id);
    persist(next);
    return { ok: true, user };
  };

  const bulkImportUsers = async (rows, actorId) => {
    const result = await bulkCreateAuthUsersWithProfiles(rows);
    const importedUsers = result.users || [];

    if (importedUsers.length) {
      const next = clone(data);
      importedUsers.forEach((incomingUser) => {
        const user = { ...incomingUser };
        normalizeUserAccess(user);
        const existingIndex = next.users.findIndex((item) => item.email?.toLowerCase() === user.email?.toLowerCase() || item.id === user.id);
        if (existingIndex >= 0) {
          next.users[existingIndex] = { ...next.users[existingIndex], ...user };
        } else {
          next.users.push(user);
        }
      });
      addAudit(next, actorId, 'bulk_imported_auth_users', 'user', String(importedUsers.length));
      persist(next);
    }

    return result;
  };

  const updateUser = (userId, patch, actorId) => {
    const next = clone(data);
    const user = next.users.find((item) => item.id === userId);
    if (!user) return { ok: false, message: 'User not found.' };
    Object.assign(user, patch, { updatedAt: new Date().toISOString() });
    normalizeUserAccess(user);
    addAudit(next, actorId, 'updated_user', 'user', userId);
    persist(next);
    return { ok: true, user };
  };

  const createGroup = (input, actorId) => {
    const next = clone(data);
    if (next.groups.some((group) => group.name.toLowerCase() === input.name.toLowerCase())) {
      return { ok: false, message: 'Group name already exists.' };
    }
    const group = {
      id: makeId('g'),
      name: input.name,
      description: input.description || '',
      status: 'active',
      memberIds: input.memberIds || []
    };
    next.groups.push(group);
    addAudit(next, actorId, 'created_group', 'group', group.id);
    persist(next);
    return { ok: true, group };
  };


  const createProgram = (input, actorId) => {
    const next = clone(data);
    const title = input.title?.trim();
    if (!title) return { ok: false, message: 'Program title is required.' };
    if (next.programs.some((program) => program.title.toLowerCase() === title.toLowerCase())) {
      return { ok: false, message: 'Program title already exists.' };
    }
    const program = {
      id: makeId('p'),
      title,
      description: input.description || '',
      category: input.category || 'Company Process',
      status: input.status || 'draft',
      createdBy: actorId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    next.programs.push(program);
    (input.courseIds || []).forEach((courseId, index) => {
      if (!next.courses.some((course) => course.id === courseId)) return;
      next.programCourses.push({
        id: makeId('pc'),
        programId: program.id,
        courseId,
        sortOrder: index + 1,
        createdAt: new Date().toISOString()
      });
    });
    addAudit(next, actorId, 'created_program', 'program', program.id);
    persist(next);
    return { ok: true, program };
  };

  const updateProgram = (programId, patch, actorId) => {
    const next = clone(data);
    const program = next.programs.find((item) => item.id === programId);
    if (!program) return { ok: false, message: 'Program not found.' };
    Object.assign(program, {
      title: patch.title ?? program.title,
      description: patch.description ?? program.description,
      category: patch.category ?? program.category,
      status: patch.status ?? program.status,
      updatedAt: new Date().toISOString()
    });
    if (Array.isArray(patch.courseIds)) {
      next.programCourses = next.programCourses.filter((item) => item.programId !== programId);
      patch.courseIds.forEach((courseId, index) => {
        if (!next.courses.some((course) => course.id === courseId)) return;
        next.programCourses.push({
          id: makeId('pc'),
          programId,
          courseId,
          sortOrder: index + 1,
          createdAt: new Date().toISOString()
        });
      });
    }
    addAudit(next, actorId, 'updated_program', 'program', programId);
    persist(next);
    return { ok: true, program };
  };

  const archiveProgram = (programId, actorId) => {
    const next = clone(data);
    const program = next.programs.find((item) => item.id === programId);
    if (!program) return { ok: false, message: 'Program not found.' };
    program.status = 'archived';
    program.updatedAt = new Date().toISOString();
    addAudit(next, actorId, 'archived_program', 'program', programId);
    persist(next);
    return { ok: true, program };
  };

  const assignCourseTrainers = (courseId, trainerIds = [], actorId) => {
    const next = clone(data);
    const course = next.courses.find((item) => item.id === courseId);
    if (!course) return { ok: false, message: 'Course not found.' };
    const cleanTrainerIds = [...new Set(trainerIds)].filter((trainerId) => {
      const user = next.users.find((item) => item.id === trainerId && item.status === 'active');
      return userCanBe(user, 'trainer') || userCanBe(user, 'admin');
    });
    course.trainerIds = cleanTrainerIds;
    if (!course.ownerId && cleanTrainerIds.length) course.ownerId = cleanTrainerIds[0];
    course.updatedAt = new Date().toISOString();
    addAudit(next, actorId, 'assigned_course_trainers', 'course', courseId);
    persist(next);
    return { ok: true, course };
  };

  const createCourse = (input, actorId) => {
    const next = clone(data);
    const courseId = makeId('c');
    const versionId = makeId('cv');
    const moduleId = makeId('m');
    const lessonId = makeId('l');
    const course = {
      id: courseId,
      title: input.title,
      description: input.description,
      category: input.category,
      targetAudience: input.targetAudience,
      difficulty: input.difficulty || 'beginner',
      estimatedMinutes: Number(input.estimatedMinutes || 60),
      ownerId: actorId,
      trainerIds: userCanBe(next.users.find((user) => user.id === actorId), 'trainer') ? [actorId] : [],
      status: 'draft',
      currentVersionId: versionId,
      certificateEnabled: Boolean(input.certificateEnabled),
      tags: input.tags ? input.tags.split(',').map((tag) => tag.trim()).filter(Boolean) : [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    next.courses.push(course);
    next.courseVersions.push({
      id: versionId,
      courseId,
      versionNumber: 1,
      status: 'draft',
      publishedAt: null,
      publishedBy: null,
      changeSummary: 'Initial draft.',
      sequential: true
    });
    next.modules.push({
      id: moduleId,
      courseVersionId: versionId,
      title: 'Module 1',
      description: 'Start building the training module here.',
      sortOrder: 1,
      isRequired: true
    });
    next.lessons.push({
      id: lessonId,
      moduleId,
      title: 'New Lesson',
      lessonType: 'reading',
      contentHtml: 'Replace this with lesson content, SOP links, instructions, or a training video.',
      videoUrl: '',
      estimatedMinutes: 15,
      completionType: 'manual_ack',
      isRequired: true,
      sortOrder: 1,
      resources: [],
      sessionDate: '',
      meetingLink: ''
    });
    addAudit(next, actorId, 'created_course', 'course', course.id);
    persist(next);
    return { ok: true, course };
  };

  const updateCourse = (courseId, patch, actorId) => {
    const next = clone(data);
    const course = next.courses.find((item) => item.id === courseId);
    if (!course) return { ok: false, message: 'Course not found.' };
    Object.assign(course, patch, { updatedAt: new Date().toISOString() });
    addAudit(next, actorId, 'updated_course', 'course', courseId);
    persist(next);
    return { ok: true, course };
  };

  const addModule = (courseVersionId, input, actorId) => {
    const next = clone(data);
    const count = next.modules.filter((module) => module.courseVersionId === courseVersionId).length;
    const module = {
      id: makeId('m'),
      courseVersionId,
      title: input.title || `Module ${count + 1}`,
      description: input.description || '',
      sortOrder: count + 1,
      isRequired: true
    };
    next.modules.push(module);
    addAudit(next, actorId, 'added_module', 'module', module.id);
    persist(next);
    return { ok: true, module };
  };

  const updateModule = (moduleId, patch, actorId) => {
    const next = clone(data);
    const module = next.modules.find((item) => item.id === moduleId);
    if (!module) return { ok: false, message: 'Module not found.' };
    Object.assign(module, {
      title: patch.title || module.title,
      description: patch.description ?? module.description
    });
    addAudit(next, actorId, 'updated_module', 'module', moduleId);
    persist(next);
    return { ok: true, module };
  };

  const addLesson = (moduleId, input, actorId) => {
    const next = clone(data);
    const count = next.lessons.filter((lesson) => lesson.moduleId === moduleId).length;
    const lesson = {
      id: makeId('l'),
      moduleId,
      title: input.title || `Lesson ${count + 1}`,
      lessonType: input.lessonType || 'reading',
      contentHtml: input.contentHtml || 'Add lesson instructions here.',
      videoUrl: input.videoUrl || '',
      estimatedMinutes: Number(input.estimatedMinutes || 20),
      completionType: input.completionType || 'manual_ack',
      isRequired: input.isRequired !== false,
      sortOrder: count + 1,
      resources: input.resources || [],
      sessionDate: input.sessionDate || '',
      meetingLink: input.meetingLink || ''
    };
    next.lessons.push(lesson);
    addAudit(next, actorId, 'added_lesson', 'lesson', lesson.id);
    persist(next);
    return { ok: true, lesson };
  };

  const updateLesson = (lessonId, patch, actorId) => {
    const next = clone(data);
    const lesson = next.lessons.find((item) => item.id === lessonId);
    if (!lesson) return { ok: false, message: 'Lesson not found.' };
    Object.assign(lesson, {
      ...patch,
      estimatedMinutes: Number(patch.estimatedMinutes || lesson.estimatedMinutes || 20),
      isRequired: patch.isRequired !== false
    });
    addAudit(next, actorId, 'updated_lesson', 'lesson', lessonId);
    persist(next);
    return { ok: true, lesson };
  };

  const addQuizQuestion = (quizId, input, actorId) => {
    const next = clone(data);
    const quiz = next.quizzes.find((item) => item.id === quizId);
    if (!quiz) return { ok: false, message: 'Quiz not found.' };
    const question = {
      id: makeId('qq'),
      questionType: 'multiple_choice',
      prompt: input.prompt,
      points: Number(input.points || 10),
      explanation: input.explanation || '',
      options: [
        { id: makeId('opt'), optionText: input.optionA || 'Option A', isCorrect: input.correct === 'A' },
        { id: makeId('opt'), optionText: input.optionB || 'Option B', isCorrect: input.correct === 'B' },
        { id: makeId('opt'), optionText: input.optionC || 'Option C', isCorrect: input.correct === 'C' }
      ]
    };
    quiz.questions.push(question);
    addAudit(next, actorId, 'added_question', 'quiz', quizId);
    persist(next);
    return { ok: true, question };
  };

  const updateQuizQuestion = (quizId, questionId, input, actorId) => {
    const next = clone(data);
    const quiz = next.quizzes.find((item) => item.id === quizId);
    if (!quiz) return { ok: false, message: 'Quiz not found.' };
    const question = quiz.questions.find((item) => item.id === questionId);
    if (!question) return { ok: false, message: 'Question not found.' };
    const existingOptions = question.options || [];
    question.prompt = input.prompt;
    question.points = Number(input.points || question.points || 10);
    question.explanation = input.explanation || '';
    question.options = [
      { id: existingOptions[0]?.id || makeId('opt'), optionText: input.optionA || 'Option A', isCorrect: input.correct === 'A' },
      { id: existingOptions[1]?.id || makeId('opt'), optionText: input.optionB || 'Option B', isCorrect: input.correct === 'B' },
      { id: existingOptions[2]?.id || makeId('opt'), optionText: input.optionC || 'Option C', isCorrect: input.correct === 'C' }
    ];
    addAudit(next, actorId, 'updated_question', 'quiz_question', questionId);
    persist(next);
    return { ok: true, question };
  };

  const addQuiz = (courseVersionId, moduleId, input, actorId) => {
    const next = clone(data);
    const placement = input.placement || (input.afterLessonId ? 'after_lesson' : 'module_end');
    const quiz = {
      id: makeId('q'),
      courseVersionId,
      moduleId: moduleId || null,
      afterLessonId: placement === 'module_end' ? null : input.afterLessonId || null,
      placement,
      title: input.title || 'Knowledge Check',
      passingScore: Number(input.passingScore || next.settings.defaultPassingScore),
      maxAttempts: Number(input.maxAttempts || next.settings.defaultMaxAttempts),
      timeLimitMinutes: Number(input.timeLimitMinutes || 15),
      questionOrder: 'fixed',
      showCorrectAnswers: 'after_attempt',
      isRequired: true,
      questions: []
    };
    next.quizzes.push(quiz);
    addAudit(next, actorId, 'created_quiz', 'quiz', quiz.id);
    persist(next);
    return { ok: true, quiz };
  };

  const submitCourseForReview = (courseId, actorId) => {
    const next = clone(data);
    const course = next.courses.find((item) => item.id === courseId);
    if (!course) return { ok: false, message: 'Course not found.' };
    course.status = 'in_review';
    course.updatedAt = new Date().toISOString();
    addAudit(next, actorId, 'submitted_course_for_review', 'course', courseId);
    persist(next);
    return { ok: true, course };
  };

  const requestCourseChanges = (courseId, actorId) => {
    const next = clone(data);
    const course = next.courses.find((item) => item.id === courseId);
    if (!course) return { ok: false, message: 'Course not found.' };
    course.status = 'changes_requested';
    course.updatedAt = new Date().toISOString();
    addAudit(next, actorId, 'requested_course_changes', 'course', courseId);
    persist(next);
    return { ok: true, course };
  };

  const publishCourse = (courseId, actorId) => {
    const next = clone(data);
    const course = next.courses.find((item) => item.id === courseId);
    if (!course) return { ok: false, message: 'Course not found.' };
    course.status = 'published';
    course.updatedAt = new Date().toISOString();
    const version = next.courseVersions.find((item) => item.id === course.currentVersionId);
    if (version) {
      version.status = 'published';
      version.publishedAt = new Date().toISOString();
      version.publishedBy = actorId;
    }
    addAudit(next, actorId, 'published_course', 'course', courseId);
    persist(next);
    return { ok: true, course };
  };

  const archiveCourse = (courseId, actorId) => {
    const next = clone(data);
    const course = next.courses.find((item) => item.id === courseId);
    if (!course) return { ok: false, message: 'Course not found.' };
    course.status = 'archived';
    course.updatedAt = new Date().toISOString();
    addAudit(next, actorId, 'archived_course', 'course', courseId);
    persist(next);
    return { ok: true, course };
  };

  const deleteCourse = (courseId, actorId) => {
    const next = clone(data);
    const actor = next.users.find((user) => user.id === actorId);
    if (!userCanBe(actor, 'admin')) {
      return { ok: false, message: 'Only admins can delete courses.' };
    }
    const course = next.courses.find((item) => item.id === courseId);
    if (!course) return { ok: false, message: 'Course not found.' };
    if (courseHasActiveAssignments(next, courseId)) {
      return { ok: false, message: 'This course still has active learner records. Revoke the assignments first, then delete the revoked assignments or archive the course instead.' };
    }

    const versionIds = next.courseVersions.filter((version) => version.courseId === courseId).map((version) => version.id);
    const moduleIds = next.modules.filter((module) => versionIds.includes(module.courseVersionId)).map((module) => module.id);
    const lessonIds = next.lessons.filter((lesson) => moduleIds.includes(lesson.moduleId)).map((lesson) => lesson.id);
    const quizIds = next.quizzes.filter((quiz) => versionIds.includes(quiz.courseVersionId)).map((quiz) => quiz.id);
    const exerciseIds = next.exercises.filter((exercise) => versionIds.includes(exercise.courseVersionId)).map((exercise) => exercise.id);

    next.courses = next.courses.filter((item) => item.id !== courseId);
    next.courseVersions = next.courseVersions.filter((version) => !versionIds.includes(version.id));
    next.modules = next.modules.filter((module) => !moduleIds.includes(module.id));
    next.lessons = next.lessons.filter((lesson) => !lessonIds.includes(lesson.id));
    next.quizzes = next.quizzes.filter((quiz) => !quizIds.includes(quiz.id));
    next.exercises = next.exercises.filter((exercise) => !exerciseIds.includes(exercise.id));
    next.assignments = next.assignments.filter((assignment) => assignment.courseId !== courseId);
    next.certificates = next.certificates.filter((certificate) => certificate.courseId !== courseId);
    addAudit(next, actorId, 'deleted_course', 'course', courseId);
    persist(next);
    return { ok: true };
  };

  const deleteModule = (moduleId, actorId) => {
    const next = clone(data);
    const module = next.modules.find((item) => item.id === moduleId);
    if (!module) return { ok: false, message: 'Module not found.' };
    if (versionHasActiveAssignments(next, module.courseVersionId)) {
      return { ok: false, message: 'This course version has active learner assignments. Do not delete modules from assigned training.' };
    }

    const lessonIds = next.lessons.filter((lesson) => lesson.moduleId === moduleId).map((lesson) => lesson.id);
    next.modules = next.modules.filter((item) => item.id !== moduleId);
    next.lessons = next.lessons.filter((lesson) => lesson.moduleId !== moduleId);
    next.quizzes = next.quizzes.filter((quiz) => quiz.moduleId !== moduleId && !lessonIds.includes(quiz.afterLessonId));
    next.exercises = next.exercises.filter((exercise) => exercise.moduleId !== moduleId);
    reindexSortOrder(next.modules.filter((item) => item.courseVersionId === module.courseVersionId));
    addAudit(next, actorId, 'deleted_module', 'module', moduleId);
    persist(next);
    return { ok: true };
  };

  const deleteLesson = (lessonId, actorId) => {
    const next = clone(data);
    const lesson = next.lessons.find((item) => item.id === lessonId);
    if (!lesson) return { ok: false, message: 'Lesson not found.' };
    const module = next.modules.find((item) => item.id === lesson.moduleId);
    if (module && versionHasActiveAssignments(next, module.courseVersionId)) {
      return { ok: false, message: 'This course version has active learner assignments. Do not delete lessons from assigned training.' };
    }

    next.lessons = next.lessons.filter((item) => item.id !== lessonId);
    next.quizzes = next.quizzes.filter((quiz) => quiz.afterLessonId !== lessonId);
    reindexSortOrder(next.lessons.filter((item) => item.moduleId === lesson.moduleId));
    addAudit(next, actorId, 'deleted_lesson', 'lesson', lessonId);
    persist(next);
    return { ok: true };
  };

  const deleteQuiz = (quizId, actorId) => {
    const next = clone(data);
    const quiz = next.quizzes.find((item) => item.id === quizId);
    if (!quiz) return { ok: false, message: 'Quiz not found.' };
    if (versionHasActiveAssignments(next, quiz.courseVersionId)) {
      return { ok: false, message: 'This course version has active learner assignments. Do not delete quizzes from assigned training.' };
    }

    next.quizzes = next.quizzes.filter((item) => item.id !== quizId);
    addAudit(next, actorId, 'deleted_quiz', 'quiz', quizId);
    persist(next);
    return { ok: true };
  };


  const addExercise = (courseVersionId, moduleId, input, actorId) => {
    const next = clone(data);
    const count = next.exercises.filter((exercise) => exercise.moduleId === moduleId).length;
    const exercise = {
      id: makeId('ex'),
      courseVersionId,
      moduleId: moduleId || null,
      title: input.title || 'Graded Exercise',
      instructions: input.instructions || '',
      exerciseFileTitle: input.exerciseFileTitle || 'Exercise file',
      exerciseFileUrl: input.exerciseFileUrl || '',
      passingScore: Number(input.passingScore || next.settings.defaultPassingScore || 80),
      maxScore: Number(input.maxScore || 100),
      isRequired: input.isRequired !== false,
      sortOrder: count + 1
    };
    next.exercises.push(exercise);
    addAudit(next, actorId, 'created_exercise', 'exercise', exercise.id);
    persist(next);
    return { ok: true, exercise };
  };

  const updateExercise = (exerciseId, patch, actorId) => {
    const next = clone(data);
    const exercise = next.exercises.find((item) => item.id === exerciseId);
    if (!exercise) return { ok: false, message: 'Exercise not found.' };
    Object.assign(exercise, {
      ...patch,
      passingScore: Number(patch.passingScore || exercise.passingScore || 80),
      maxScore: Number(patch.maxScore || exercise.maxScore || 100),
      isRequired: patch.isRequired !== false
    });
    addAudit(next, actorId, 'updated_exercise', 'exercise', exerciseId);
    persist(next);
    return { ok: true, exercise };
  };

  const deleteExercise = (exerciseId, actorId) => {
    const next = clone(data);
    const exercise = next.exercises.find((item) => item.id === exerciseId);
    if (!exercise) return { ok: false, message: 'Exercise not found.' };
    if (versionHasActiveAssignments(next, exercise.courseVersionId)) {
      return { ok: false, message: 'This course version has active learner assignments. Do not delete exercises from assigned training.' };
    }
    next.exercises = next.exercises.filter((item) => item.id !== exerciseId);
    addAudit(next, actorId, 'deleted_exercise', 'exercise', exerciseId);
    persist(next);
    return { ok: true };
  };

  const deleteQuizQuestion = (quizId, questionId, actorId) => {
    const next = clone(data);
    const quiz = next.quizzes.find((item) => item.id === quizId);
    if (!quiz) return { ok: false, message: 'Quiz not found.' };
    if (versionHasActiveAssignments(next, quiz.courseVersionId)) {
      return { ok: false, message: 'This course version has active learner assignments. Do not delete questions from assigned training.' };
    }
    const existingCount = quiz.questions.length;
    quiz.questions = quiz.questions.filter((question) => question.id !== questionId);
    if (quiz.questions.length === existingCount) return { ok: false, message: 'Question not found.' };

    addAudit(next, actorId, 'deleted_question', 'quiz_question', questionId);
    persist(next);
    return { ok: true };
  };

  const assignCourse = ({ courseId, userIds = [], groupIds = [], dueDate, required = true, programId = '', programAssignmentId = '' }, actorId) => {
    if (!String(dueDate || '').trim()) return { ok: false, message: 'Due date is required before assigning a course.' };
    const next = clone(data);
    const course = next.courses.find((item) => item.id === courseId);
    if (!course || course.status !== 'published') return { ok: false, message: 'Only published courses can be assigned.' };

    const learnerIds = new Set(userIds);
    groupIds.forEach((groupId) => {
      const group = next.groups.find((item) => item.id === groupId);
      (group?.memberIds || []).forEach((memberId) => learnerIds.add(memberId));
    });

    const created = [];
    learnerIds.forEach((learnerId) => {
      const learner = next.users.find((user) => user.id === learnerId && userCanBe(user, 'learner') && user.status === 'active');
      if (!learner) return;
      const duplicate = next.assignments.some(
        (assignment) => assignment.assignedToUserId === learnerId && assignment.courseId === courseId && assignment.status !== 'revoked'
      );
      if (duplicate) return;
      const assignment = {
        id: makeId('a'),
        courseId,
        courseVersionId: course.currentVersionId,
        assignedToUserId: learnerId,
        assignedByUserId: actorId,
        programId,
        programAssignmentId,
        assignmentSource: programId ? 'program' : groupIds.length ? 'group' : 'direct',
        dueDate: dueDate || '',
        required: Boolean(required),
        status: 'not_started',
        startedAt: null,
        completedAt: null,
        lessonProgress: normalizeLessonProgress(next, course.currentVersionId),
        quizAttempts: [],
        quizAnswerDrafts: {},
        exerciseSubmissions: []
      };
      next.assignments.push(assignment);
      created.push(assignment);
      addAudit(next, actorId, 'assigned_course', 'assignment', assignment.id);
    });

    persist(next);
    return { ok: true, created };
  };


  const assignProgram = ({ programId, userIds = [], groupIds = [], dueDate, required = true }, actorId) => {
    if (!String(dueDate || '').trim()) return { ok: false, message: 'Due date is required before assigning a program.' };
    const next = clone(data);
    const program = next.programs.find((item) => item.id === programId);
    if (!program || program.status !== 'published') return { ok: false, message: 'Only published programs can be assigned.' };
    const programCourses = next.programCourses
      .filter((item) => item.programId === programId)
      .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
      .map((item) => next.courses.find((course) => course.id === item.courseId))
      .filter((course) => course && course.status === 'published');
    if (!programCourses.length) return { ok: false, message: 'This program has no published courses to assign.' };

    const learnerIds = new Set(userIds);
    groupIds.forEach((groupId) => {
      const group = next.groups.find((item) => item.id === groupId);
      (group?.memberIds || []).forEach((memberId) => learnerIds.add(memberId));
    });

    const createdProgramAssignments = [];
    const createdCourseAssignments = [];
    learnerIds.forEach((learnerId) => {
      const learner = next.users.find((user) => user.id === learnerId && userCanBe(user, 'learner') && user.status === 'active');
      if (!learner) return;
      const duplicateProgram = next.programAssignments.some(
        (assignment) => assignment.assignedToUserId === learnerId && assignment.programId === programId && assignment.status !== 'revoked'
      );
      if (duplicateProgram) return;

      const programAssignment = {
        id: makeId('pa'),
        programId,
        assignedToUserId: learnerId,
        assignedByUserId: actorId,
        assignedToGroupId: groupIds[0] || '',
        assignmentSource: groupIds.length ? 'group' : 'direct',
        dueDate: dueDate || '',
        required: Boolean(required),
        status: 'not_started',
        assignedAt: new Date().toISOString(),
        completedAt: null
      };
      next.programAssignments.push(programAssignment);
      createdProgramAssignments.push(programAssignment);

      programCourses.forEach((course) => {
        const duplicateCourse = next.assignments.some(
          (assignment) => assignment.assignedToUserId === learnerId && assignment.courseId === course.id && assignment.status !== 'revoked'
        );
        if (duplicateCourse) return;
        const assignment = {
          id: makeId('a'),
          courseId: course.id,
          courseVersionId: course.currentVersionId,
          assignedToUserId: learnerId,
          assignedByUserId: actorId,
          programId,
          programAssignmentId: programAssignment.id,
          assignmentSource: 'program',
          dueDate: dueDate || '',
          required: Boolean(required),
          status: 'not_started',
          startedAt: null,
          completedAt: null,
          lessonProgress: normalizeLessonProgress(next, course.currentVersionId),
          quizAttempts: [],
          quizAnswerDrafts: {},
          exerciseSubmissions: []
        };
        next.assignments.push(assignment);
        createdCourseAssignments.push(assignment);
      });
      addAudit(next, actorId, 'assigned_program', 'program_assignment', programAssignment.id);
    });

    persist(next);
    return { ok: true, created: createdProgramAssignments, createdCourseAssignments };
  };

  const revokeAssignment = (assignmentId, actorId) => {
    const next = clone(data);
    const assignment = next.assignments.find((item) => item.id === assignmentId);
    if (!assignment) return { ok: false, message: 'Assignment not found.' };
    assignment.status = 'revoked';
    addAudit(next, actorId, 'revoked_assignment', 'assignment', assignmentId);
    persist(next);
    return { ok: true };
  };

  const revokeProgramAssignment = (programAssignmentId, actorId) => {
    const next = clone(data);
    const programAssignment = next.programAssignments.find((item) => item.id === programAssignmentId);
    if (!programAssignment) return { ok: false, message: 'Program assignment not found.' };
    programAssignment.status = 'revoked';
    next.assignments
      .filter((assignment) => assignment.programAssignmentId === programAssignmentId)
      .forEach((assignment) => { assignment.status = 'revoked'; });
    addAudit(next, actorId, 'revoked_program_assignment', 'program_assignment', programAssignmentId);
    persist(next);
    return { ok: true };
  };

  const deleteAssignment = (assignmentId, actorId) => {
    const next = clone(data);
    const assignment = next.assignments.find((item) => item.id === assignmentId);
    if (!assignment) return { ok: false, message: 'Assignment not found.' };
    if (assignment.status !== 'revoked') return { ok: false, message: 'Only revoked assignments can be deleted.' };
    next.assignments = next.assignments.filter((item) => item.id !== assignmentId);
    next.certificates = next.certificates.filter((certificate) => certificate.assignmentId !== assignmentId);
    addAudit(next, actorId, 'deleted_revoked_assignment', 'assignment', assignmentId);
    persist(next);
    return { ok: true };
  };

  const deleteProgramAssignment = (programAssignmentId, actorId) => {
    const next = clone(data);
    const programAssignment = next.programAssignments.find((item) => item.id === programAssignmentId);
    if (!programAssignment) return { ok: false, message: 'Program assignment not found.' };
    if (programAssignment.status !== 'revoked') return { ok: false, message: 'Only revoked program assignments can be deleted.' };
    const assignmentIds = next.assignments.filter((item) => item.programAssignmentId === programAssignmentId).map((item) => item.id);
    next.programAssignments = next.programAssignments.filter((item) => item.id !== programAssignmentId);
    next.assignments = next.assignments.filter((item) => item.programAssignmentId !== programAssignmentId);
    next.certificates = next.certificates.filter((certificate) => !assignmentIds.includes(certificate.assignmentId));
    addAudit(next, actorId, 'deleted_revoked_program_assignment', 'program_assignment', programAssignmentId);
    persist(next);
    return { ok: true };
  };

  const updateAssignmentDueDate = (assignmentId, dueDate, actorId) => {
    const cleanedDueDate = String(dueDate || '').trim();
    if (!cleanedDueDate) return { ok: false, message: 'Due date is required.' };
    const next = clone(data);
    const assignment = next.assignments.find((item) => item.id === assignmentId);
    if (!assignment) return { ok: false, message: 'Assignment not found.' };

    if (assignment.programAssignmentId) {
      const relatedAssignments = next.assignments.filter((item) => item.programAssignmentId === assignment.programAssignmentId);
      relatedAssignments.forEach((item) => { item.dueDate = cleanedDueDate; });
      const programAssignment = next.programAssignments.find((item) => item.id === assignment.programAssignmentId);
      if (programAssignment) programAssignment.dueDate = cleanedDueDate;
      addAudit(next, actorId, 'updated_program_assignment_due_date', 'program_assignment', assignment.programAssignmentId);
    } else {
      assignment.dueDate = cleanedDueDate;
      addAudit(next, actorId, 'updated_assignment_due_date', 'assignment', assignmentId);
    }

    persist(next);
    return { ok: true };
  };

  const updateProgramAssignmentDueDate = (programAssignmentId, dueDate, actorId) => {
    const cleanedDueDate = String(dueDate || '').trim();
    if (!cleanedDueDate) return { ok: false, message: 'Due date is required.' };
    const next = clone(data);
    const programAssignment = next.programAssignments.find((item) => item.id === programAssignmentId);
    if (!programAssignment) return { ok: false, message: 'Program assignment not found.' };

    programAssignment.dueDate = cleanedDueDate;
    next.assignments
      .filter((assignment) => assignment.programAssignmentId === programAssignmentId)
      .forEach((assignment) => { assignment.dueDate = cleanedDueDate; });
    addAudit(next, actorId, 'updated_program_assignment_due_date', 'program_assignment', programAssignmentId);
    persist(next);
    return { ok: true };
  };

  const completeLesson = async (assignmentId, lessonId, input, actorId) => {
    const next = clone(data);
    const assignment = next.assignments.find((item) => item.id === assignmentId);
    if (!assignment) return { ok: false, message: 'Assignment not found.' };
    const lesson = next.lessons.find((item) => item.id === lessonId);
    if (!lesson) return { ok: false, message: 'Lesson not found.' };
    if (lesson.lessonType === 'practical') {
      return { ok: false, message: 'Practical Activity completion requires trainer attendance confirmation.' };
    }
    assignment.startedAt = assignment.startedAt || new Date().toISOString();
    assignment.lessonProgress[lessonId] = assignment.lessonProgress[lessonId] || { status: 'not_started', acknowledged: false };
    if (lesson.completionType === 'trainer_approval') {
      assignment.lessonProgress[lessonId] = {
        ...assignment.lessonProgress[lessonId],
        status: 'needs_review',
        acknowledged: true,
        submissionText: input?.submissionText || 'Submitted for trainer review.',
        submissionUrl: input?.submissionUrl || '',
        submittedAt: new Date().toISOString()
      };
    } else {
      assignment.lessonProgress[lessonId] = {
        ...assignment.lessonProgress[lessonId],
        status: 'completed',
        acknowledged: true,
        submissionText: input?.submissionText || '',
        submissionUrl: input?.submissionUrl || '',
        completedAt: new Date().toISOString()
      };
    }
    recalculateAssignment(next, assignmentId);
    const audit = addAudit(next, actorId, 'completed_lesson', 'lesson_progress', lessonId);
    const saveResult = await persistLearningMutation(next, assignmentId, [audit]);
    if (!saveResult.ok) return saveResult;
    return { ok: true };
  };

  const approveLessonSubmission = async (assignmentId, lessonId, actorId, feedback = 'Approved') => {
    const next = clone(data);
    const assignment = next.assignments.find((item) => item.id === assignmentId);
    if (!assignment) return { ok: false, message: 'Assignment not found.' };
    assignment.lessonProgress[lessonId] = {
      ...(assignment.lessonProgress[lessonId] || {}),
      status: 'completed',
      feedback,
      reviewedBy: actorId,
      completedAt: new Date().toISOString()
    };
    recalculateAssignment(next, assignmentId);
    const audit = addAudit(next, actorId, 'approved_practical_activity', 'lesson_progress', lessonId);
    const saveResult = await persistLearningMutation(next, assignmentId, [audit]);
    if (!saveResult.ok) return saveResult;
    return { ok: true };
  };

  const rejectLessonSubmission = async (assignmentId, lessonId, actorId, feedback = 'Needs revision') => {
    const next = clone(data);
    const assignment = next.assignments.find((item) => item.id === assignmentId);
    if (!assignment) return { ok: false, message: 'Assignment not found.' };
    assignment.lessonProgress[lessonId] = {
      ...(assignment.lessonProgress[lessonId] || {}),
      status: 'in_progress',
      feedback,
      reviewedBy: actorId
    };
    recalculateAssignment(next, assignmentId);
    const audit = addAudit(next, actorId, 'rejected_practical_activity', 'lesson_progress', lessonId);
    const saveResult = await persistLearningMutation(next, assignmentId, [audit]);
    if (!saveResult.ok) return saveResult;
    return { ok: true };
  };


  const submitExercise = async (assignmentId, exerciseId, input, actorId) => {
    const next = clone(data);
    const assignment = next.assignments.find((item) => item.id === assignmentId);
    const exercise = next.exercises.find((item) => item.id === exerciseId);
    if (!assignment || !exercise) return { ok: false, message: 'Exercise or assignment not found.' };
    const submissions = assignment.exerciseSubmissions?.filter((submission) => submission.exerciseId === exerciseId) || [];
    const latest = latestExerciseSubmission(assignment, exerciseId);
    if (latest?.status === 'needs_review') {
      return { ok: false, message: 'This exercise is already submitted and waiting for trainer review.' };
    }
    assignment.startedAt = assignment.startedAt || new Date().toISOString();
    assignment.exerciseSubmissions = assignment.exerciseSubmissions || [];
    const submission = {
      id: makeId('exa'),
      exerciseId,
      attemptNumber: submissions.length + 1,
      status: 'needs_review',
      submissionUrl: input?.submissionUrl || '',
      submissionText: input?.submissionText || '',
      score: null,
      scorePercent: null,
      feedback: '',
      submittedAt: new Date().toISOString(),
      reviewedAt: null,
      reviewedBy: ''
    };
    assignment.exerciseSubmissions.push(submission);
    recalculateAssignment(next, assignmentId);
    const audit = addAudit(next, actorId, 'submitted_exercise', 'exercise_submission', submission.id);
    const saveResult = await persistLearningMutation(next, assignmentId, [audit]);
    if (!saveResult.ok) return saveResult;
    return { ok: true, submission };
  };

  const reviewExerciseSubmission = async (assignmentId, submissionId, actorId, input = {}) => {
    const next = clone(data);
    const assignment = next.assignments.find((item) => item.id === assignmentId);
    if (!assignment) return { ok: false, message: 'Assignment not found.' };
    const submission = assignment.exerciseSubmissions?.find((item) => item.id === submissionId);
    if (!submission) return { ok: false, message: 'Exercise submission not found.' };
    const exercise = next.exercises.find((item) => item.id === submission.exerciseId);
    const maxScore = Number(exercise?.maxScore || 100);
    const score = Math.max(0, Math.min(Number(input.score ?? 0), maxScore));
    const scorePercent = maxScore ? Math.round((score / maxScore) * 100) : 0;
    const passed = scorePercent >= Number(exercise?.passingScore || 80);
    Object.assign(submission, {
      score,
      scorePercent,
      status: passed ? 'passed' : 'failed',
      feedback: input.feedback || (passed ? 'Approved.' : 'Needs improvement. Please revise and resubmit.'),
      reviewedAt: new Date().toISOString(),
      reviewedBy: actorId
    });
    recalculateAssignment(next, assignmentId);
    const audit = addAudit(next, actorId, 'reviewed_exercise', 'exercise_submission', submissionId);
    const saveResult = await persistLearningMutation(next, assignmentId, [audit]);
    if (!saveResult.ok) return saveResult;
    return { ok: true, submission };
  };

  const saveQuizAnswers = async (assignmentId, quizId, answers, actorId) => {
    const next = clone(data);
    const assignment = next.assignments.find((item) => item.id === assignmentId);
    const quiz = next.quizzes.find((item) => item.id === quizId);
    if (!assignment || !quiz) return { ok: false, message: 'Quiz or assignment not found.' };
    const gate = getQuizAvailability(next, assignment, quiz);
    if (!gate.canTake) {
      return { ok: false, message: gate.message || 'Complete the attached lesson or module before saving quiz answers.' };
    }

    const cleanAnswers = Object.fromEntries(
      Object.entries(answers || {}).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
    if (!Object.keys(cleanAnswers).length) {
      return { ok: false, message: 'Choose at least one answer before saving.' };
    }

    assignment.startedAt = assignment.startedAt || new Date().toISOString();
    assignment.quizAnswerDrafts = assignment.quizAnswerDrafts || {};
    const draft = {
      quizId,
      answers: cleanAnswers,
      savedAt: new Date().toISOString()
    };
    assignment.quizAnswerDrafts[quizId] = draft;
    recalculateAssignment(next, assignmentId);
    const audit = addAudit(next, actorId, 'saved_quiz_answers', 'quiz_draft', `${assignmentId}:${quizId}`);
    const saveResult = await persistLearningMutation(next, assignmentId, [audit]);
    if (!saveResult.ok) return saveResult;
    return { ok: true, draft };
  };

  const submitQuiz = async (assignmentId, quizId, answers, actorId) => {
    const next = clone(data);
    const assignment = next.assignments.find((item) => item.id === assignmentId);
    const quiz = next.quizzes.find((item) => item.id === quizId);
    if (!assignment || !quiz) return { ok: false, message: 'Quiz or assignment not found.' };
    const gate = getQuizAvailability(next, assignment, quiz);
    if (!gate.canTake) {
      return { ok: false, message: gate.message || 'Complete the attached lesson or module before taking this quiz.' };
    }

    assignment.quizAttempts = assignment.quizAttempts || [];
    assignment.quizAnswerDrafts = assignment.quizAnswerDrafts || {};
    const attempts = assignment.quizAttempts.filter((attempt) => attempt.quizId === quizId);
    if (attempts.length >= Number(quiz.maxAttempts || 1) && !quizPassed(assignment, quiz)) {
      const reset = resetQuizLearningScope(next, assignment, quiz);
      recalculateAssignment(next, assignmentId);
      const audit = addAudit(next, actorId, 'auto_reset_quiz_scope', reset.type, quizId);
      const saveResult = await persistLearningMutation(next, assignmentId, [audit]);
      if (!saveResult.ok) return saveResult;
      return {
        ok: false,
        reset,
        message: `Maximum attempts were already reached. ${reset.type === 'lesson' ? 'The attached lesson' : reset.type === 'module' ? 'The attached module' : 'This quiz'} was reset automatically, and quiz attempts were cleared. Review the content and retake the quiz.`
      };
    }

    let earned = 0;
    let total = 0;
    quiz.questions.forEach((question) => {
      total += Number(question.points || 0);
      const selected = answers[question.id];
      const correctOption = question.options.find((option) => option.isCorrect);
      if (selected === correctOption?.id) earned += Number(question.points || 0);
    });

    const scorePercent = total ? Math.round((earned / total) * 100) : 0;
    const passed = scorePercent >= Number(quiz.passingScore || 80);
    const attempt = {
      id: makeId('qa'),
      quizId,
      attemptNumber: attempts.length + 1,
      status: passed ? 'passed' : 'failed',
      scorePercent,
      answers,
      submittedAt: new Date().toISOString()
    };
    assignment.startedAt = assignment.startedAt || new Date().toISOString();
    assignment.quizAttempts.push(attempt);
    delete assignment.quizAnswerDrafts[quizId];

    let resetAudit = null;
    if (!passed && attempt.attemptNumber >= Number(quiz.maxAttempts || 1)) {
      const reset = resetQuizLearningScope(next, assignment, quiz);
      attempt.resetTriggered = true;
      attempt.reset = reset;
      attempt.feedback = `${reset.type === 'lesson' ? 'The attached lesson' : reset.type === 'module' ? 'The attached module' : 'This quiz'} was reset automatically because the maximum quiz attempts were reached. Trainer review is not required.`;
      resetAudit = addAudit(next, actorId, 'auto_reset_quiz_scope', reset.type, quizId);
    }

    recalculateAssignment(next, assignmentId);
    const submittedAudit = addAudit(next, actorId, 'submitted_quiz', 'quiz_attempt', attempt.id);
    const saveResult = await persistLearningMutation(next, assignmentId, [resetAudit, submittedAudit].filter(Boolean));
    if (!saveResult.ok) return saveResult;
    return { ok: true, attempt };
  };

  const revokeCertificate = (certificateId, actorId) => {
    const next = clone(data);
    const certificate = next.certificates.find((item) => item.id === certificateId);
    if (!certificate) return { ok: false, message: 'Certificate not found.' };
    certificate.status = 'revoked';
    addAudit(next, actorId, 'revoked_certificate', 'certificate', certificateId);
    persist(next);
    return { ok: true };
  };

  const updateSettings = (patch, actorId) => {
    const next = clone(data);
    next.settings = { ...next.settings, ...patch };
    addAudit(next, actorId, 'updated_settings', 'settings', 'global');
    persist(next);
    return { ok: true };
  };

  const checkSupabaseConnection = async () => {
    const result = await testSupabaseConnection();
    setStorageStatus((previous) => ({
      ...previous,
      message: result.message,
      error: result.ok ? '' : result.message,
      lastSyncedAt: result.ok ? new Date().toISOString() : previous.lastSyncedAt
    }));
    return result;
  };

  if (isInitializing) {
    return (
      <div className="app-loading app-loading-quiet" aria-label="Loading LMS">
        <div className="loading-orb" />
      </div>
    );
  }

  if (storageStatus.error && !data.users.length) {
    return (
      <div className="app-loading">
        <div className="loading-card">
          <h1>Supabase setup required</h1>
          <p>{storageStatus.error}</p>
          <p>Add your Supabase environment variables, run the setup SQL in Supabase, then restart the app.</p>
        </div>
      </div>
    );
  }

  const value = {
    data,
    currentUser,
    storageStatus,
    isSupabaseConfigured,
    checkSupabaseConnection,
    login,
    logout,
    createUser,
    bulkImportUsers,
    updateUser,
    createGroup,
    createProgram,
    updateProgram,
    archiveProgram,
    assignProgram,
    createCourse,
    updateCourse,
    assignCourseTrainers,
    addModule,
    updateModule,
    addLesson,
    updateLesson,
    addQuiz,
    addQuizQuestion,
    addExercise,
    updateExercise,
    deleteExercise,
    updateQuizQuestion,
    submitCourseForReview,
    requestCourseChanges,
    publishCourse,
    archiveCourse,
    deleteCourse,
    deleteModule,
    deleteLesson,
    deleteQuiz,
    deleteQuizQuestion,
    assignCourse,
    revokeAssignment,
    revokeProgramAssignment,
    deleteAssignment,
    deleteProgramAssignment,
    updateAssignmentDueDate,
    updateProgramAssignmentDueDate,
    completeLesson,
    approveLessonSubmission,
    rejectLessonSubmission,
    submitExercise,
    reviewExerciseSubmission,
    saveQuizAnswers,
    submitQuiz,
    revokeCertificate,
    updateSettings,
    selectors: {
      assignmentGrade,
      byId,
      getAssignmentProgress,
      getCourse,
      getExercisesForVersion,
      getLessonsForVersion,
      getQuizzesForVersion
    }
  };

  return <LmsContext.Provider value={value}>{children}</LmsContext.Provider>;
}

export function useLms() {
  const context = useContext(LmsContext);
  if (!context) throw new Error('useLms must be used inside LmsProvider');
  return context;
}
